/* eslint-disable no-console */
import { Button, Col, Divider, Form, Input, Row, Space } from "antd";
import Heading from "components/Common/Heading/Heading";
import React, { useState } from "react";
import "./UserProfile.scss";
import ChangePassword from "./ChangePassword";
import { updateUserProfile, updateUsername } from "api/services/userAuth.api";
import { notificationController } from "components/Common/Notification/Notification";
import { UserProfileFormData, UsersData } from "interfaces/ApiInterface/ILoginFormData";
import { useNavigate } from "react-router-dom";
import { ROUTES_PATH } from "routes/Routes.Constant";

// eslint-disable-next-line max-lines-per-function
const UserProfile = () => {
  const [isLoading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // Retrieving user record from local storage
  const userString = localStorage.getItem("UserRecord");
  const userRecord = userString ? JSON.parse(userString) : null;

  const performProfileUpdate = async (updatedUser: UsersData): Promise<void> => {
    try {
      await updateUserProfile(updatedUser);
      localStorage.setItem("UserRecord", JSON.stringify(updatedUser));
      const userSet = document.getElementById("userNameHeader");
      if (userSet) userSet.innerHTML = `${updatedUser.FirstName}`;

      notificationController.success({
        message: "Profile updated successfully.",
      });
    } catch (error) {
      console.error("Failed to update profile", error);
      throw new Error("Profile update failed.");
    } finally {
      setLoading(false);
    }
  };

  const logoutHandler = (): void => {
    localStorage.removeItem("UserRecord");
    navigate(ROUTES_PATH.HOMEPAGE);
  };

  const handleSubmit = async (values: UserProfileFormData): Promise<void> => {
    if (!userRecord) return;
    setLoading(true);

    const updatedUser: UsersData = {
      ...userRecord,
      Email: values.email || userRecord.Email,
      UserName: values.username || userRecord.UserName,
      FirstName: values.firstName || userRecord.FirstName,
      LastName: values.lastName || userRecord.LastName,
      PhoneNumber: values.phoneNumber || userRecord.PhoneNumber,
    };

    try {
      // Check if username has changed
      if (updatedUser.UserName !== userRecord.UserName) {
        const usernameUpdateData = {
          UserName: updatedUser.UserName,
          UserId: updatedUser.UserId,
          PortalId: userRecord.PortalId,
        };
        await updateUsername(usernameUpdateData);
        await performProfileUpdate(updatedUser);
        logoutHandler(); // Call logout handler if username is successfully updated
      } else {
        await performProfileUpdate(updatedUser);
        // Profile updated successfully, no need to logout
      }
    } catch (error) {
      notificationController.error({
        message: "Profile update failed.",
      });
    }
  };

  const handleCancel = () => {
    navigate(ROUTES_PATH.HOMEPAGE);
  };

  return (
    <div className="page-content-wrapper p-30">
      <Row gutter={15} className="profile-form-container">
        <Col span={12} xs={24} md={12} xxl={12} lg={12}>
          <Heading titleText="Edit Profile" titleClass="profile-heading" />
          <div className="form-wrapper">
            <Form
              layout="vertical"
              name="userProfile"
              form={form}
              initialValues={{
                username: userRecord?.UserName || "",
                firstName: userRecord?.FirstName || "",
                lastName: userRecord?.LastName || "",
                phoneNumber: userRecord?.PhoneNumber || "",
                email: userRecord?.Email || "",
              }}
              autoComplete="off"
              onFinish={handleSubmit}
            >
              <Form.Item
                label={
                  <span className="wrapper">
                    Username <span className="text-danger">*</span>
                  </span>
                }
                name="username"
                rules={[
                  {
                    required: true,
                    message: "Username/Email address is required",
                  },
                  { type: "email", message: "Please enter a valid Username/Email address." },
                ]}
                className="input-label"
              >
                <Input className="form-input" />
              </Form.Item>
              <Form.Item
                label={
                  <span className="wrapper">
                    First Name <span className="text-danger">*</span>
                  </span>
                }
                name="firstName"
                rules={[
                  {
                    required: true,
                    message: "Please enter the first name.",
                  },
                ]}
                className="input-label"
              >
                <Input className="form-input" />
              </Form.Item>
              <Form.Item
                label={
                  <span className="wrapper">
                    Last Name <span className="text-danger">*</span>
                  </span>
                }
                name="lastName"
                rules={[
                  {
                    required: true,
                    message: "Please enter the last name.",
                  },
                ]}
                className="input-label"
              >
                <Input className="form-input" />
              </Form.Item>
              <Form.Item
                label={
                  <span className="wrapper">
                    Phone Number <span className="text-danger">*</span>
                  </span>
                }
                name="phoneNumber"
                rules={[
                  {
                    required: true,
                    message: "Please enter the phone number.",
                  },
                  {
                    pattern: new RegExp(/^\d+$/),
                    message: "Phone number must be numeric.",
                  },
                ]}
                className="input-label"
              >
                <Input className="form-input" />
              </Form.Item>
              <Form.Item
                label={
                  <span className="wrapper">
                    Email Address <span className="text-danger">*</span>
                  </span>
                }
                name="email"
                rules={[
                  { required: true, message: "Email address is required." },
                  { type: "email", message: "Please enter a valid email address." },
                ]}
              >
                <Input className="form-input" />
              </Form.Item>

              <Form.Item className="profile-action">
                <Space size={"middle"}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    className={isLoading ? "btn-disabled profile-btn" : "profile-btn"}
                  >
                    Update Profile
                  </Button>
                  <Button className="cancel-btn" onClick={handleCancel}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
      <Divider />
      <ChangePassword />
    </div>
  );
};

export default UserProfile;
